import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  PDFDownloadLink,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Document,
  Page,
  Image,
} from "@react-pdf/renderer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchClientStatement } from "../../../../store/thunks/reportsThunk";
import SearchDropdown from "../../../../applicationUi/components/SearchDropdown";

// PDF Layout Constants - Precise measurements for predictable pagination
const PAGE_HEIGHT = 792; // US Letter size in points
const PAGE_WIDTH = 612; // US Letter width in points
const MARGIN = 40; // Standard margins
const HEADER_HEIGHT = 80; // Fixed header height
const FOOTER_HEIGHT = 40; // Fixed footer height  
const TABLE_HEADER_HEIGHT = 20; // Table header row height
const ROW_HEIGHT = 18; // Fixed row height for consistency - increased to ensure text visibility
const SUMMARY_HEIGHT = 60; // Fixed summary section height

// Font settings for consistency - Using monospace for predictable text calculations
const FONT_FAMILY = 'Courier';
const FONT_SIZE_LARGE = 14; // Headers and titles
const FONT_SIZE_NORMAL = 10; // Body text
const FONT_SIZE_SMALL = 8; // Footer and fine print

// Calculate available content space
const CONTENT_HEIGHT = PAGE_HEIGHT - 2 * MARGIN - HEADER_HEIGHT - FOOTER_HEIGHT;
const FIRST_PAGE_CLIENT_INFO_HEIGHT = 60; // Increased space for client info on first page, especially split billing
const FIRST_PAGE_CONTENT_HEIGHT = CONTENT_HEIGHT - FIRST_PAGE_CLIENT_INFO_HEIGHT;

// Calculate exact rows per page (adjusted based on real testing)
const ROWS_PER_FIRST_PAGE = 28; // Confirmed by testing - fits perfectly
const ROWS_PER_CONTINUATION_PAGE = 34; // Reduced from 35 - rows 63-64 were overlapping footer

const styles = StyleSheet.create({
  // Page layout
  page: {
    fontFamily: FONT_FAMILY,
    padding: MARGIN,
    fontSize: FONT_SIZE_NORMAL,
  },
  
  // Header styles
  header: {
    height: HEADER_HEIGHT,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#CCCCCC",
    paddingBottom: 10,
  },
  logo: {
    width: 80,
    height: 40,
  },
  clinicInfo: {
    alignItems: "flex-end",
    flex: 1,
  },
  clinicName: {
    fontSize: FONT_SIZE_LARGE,
    fontWeight: "bold",
    color: "#0066CC",
    marginBottom: 2,
  },
  clinicDetails: {
    fontSize: FONT_SIZE_SMALL,
    color: "#333333",
    marginBottom: 1,
    textAlign: "right",
  },
  title: {
    fontSize: FONT_SIZE_LARGE + 2,
    fontWeight: "bold",
    color: "#0066CC",
    textAlign: "center",
    marginTop: 5, // Reduced from 10 to give more space for client info
  },
  
  // Footer styles
  footer: {
    position: "absolute",
    bottom: MARGIN,
    left: MARGIN,
    right: MARGIN,
    height: FOOTER_HEIGHT,
    borderTopWidth: 1,
    borderTopColor: "#CCCCCC",
    paddingTop: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  footerText: {
    fontSize: FONT_SIZE_SMALL,
    color: "#666666",
    textAlign: "center",
    marginBottom: 2,
  },
  
  // Content styles
  clientInfoSection: {
    height: FIRST_PAGE_CLIENT_INFO_HEIGHT,
    marginBottom: 10,
  },
  dateRange: {
    fontSize: FONT_SIZE_NORMAL,
    textAlign: "center",
    marginBottom: 8,
    fontWeight: "bold",
  },
  clientInfo: {
    fontSize: FONT_SIZE_NORMAL,
    marginBottom: 2, // Further reduced spacing to prevent overlap
    lineHeight: 1.2, // Add line height for better text spacing
  },
  clientInfoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 3, // Reduced from 5
  },
  primaryClientColumn: {
    width: "60%",
    paddingRight: 10,
  },
  relatedClientsColumn: {
    width: "40%",
    paddingLeft: 10,
  },
  relatedClientsTitle: {
    fontSize: FONT_SIZE_NORMAL,
    fontWeight: "bold",
    marginBottom: 2, // Reduced spacing
    color: "#333333",
    lineHeight: 1.2,
  },
  relatedClientItem: {
    fontSize: FONT_SIZE_SMALL,
    marginBottom: 1, // Reduced spacing
    color: "#555555",
    lineHeight: 1.1,
  },
  
  // Table styles
  tableHeader: {
    height: TABLE_HEADER_HEIGHT,
    flexDirection: "row",
    backgroundColor: "#F0F0F0",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000000",
    alignItems: "center",
    paddingHorizontal: 4,
  },
  tableHeaderText: {
    fontWeight: "bold",
    fontSize: FONT_SIZE_NORMAL,
    color: "#000000",
  },
  
  // Table row styles - removed paddingVertical to fix spacing math
  row: {
    flexDirection: "row",
    alignItems: "flex-start", // Top-align all columns when text wraps
    paddingHorizontal: 4,
    // Removed paddingVertical to fix uneven top/bottom spacing that throws off calculations
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
    justifyContent: "flex-start",
    minHeight: ROW_HEIGHT, // Minimum height but allow expansion for dynamic content
  },
  
  // Column styles with exact widths and tighter text alignment for dynamic height
  dateCol: { 
    width: "12%", 
    fontSize: FONT_SIZE_NORMAL,
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding for better spacing control
  },
  typeCol: { 
    width: "18%", 
    fontSize: FONT_SIZE_NORMAL,
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding for better spacing control
  },
  descCol: { 
    width: "40%", 
    fontSize: FONT_SIZE_NORMAL,
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding for better spacing control
  },
  chargeCol: { 
    width: "15%", 
    fontSize: FONT_SIZE_NORMAL, 
    textAlign: "right",
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding for better spacing control
  },
  paymentCol: { 
    width: "15%", 
    fontSize: FONT_SIZE_NORMAL, 
    textAlign: "right",
    lineHeight: 1.1, // Tighter line height to reduce gaps
    paddingTop: 2 // Reduced padding for better spacing control
  },
  
  // Summary styles
  summarySection: {
    height: SUMMARY_HEIGHT,
    borderTopWidth: 2,
    borderTopColor: "#000000",
    paddingTop: 8,
    justifyContent: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 3,
  },
  summaryLabel: {
    width: "70%",
    textAlign: "right",
    fontSize: FONT_SIZE_NORMAL,
    fontWeight: "bold",
    paddingRight: 10,
  },
  summaryValue: {
    width: "30%",
    textAlign: "right",
    fontSize: FONT_SIZE_NORMAL,
    fontWeight: "bold",
  },
  balanceRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    paddingTop: 5,
    marginTop: 5,
  },
  
  // No transactions message
  noTransactionsRow: {
    height: ROW_HEIGHT * 3,
    justifyContent: "center",
    alignItems: "center",
  },
  noTransactionsText: {
    fontSize: FONT_SIZE_NORMAL,
    color: "#666666",
    fontStyle: "italic",
  },
});

// Create wrappable description text with forced line breaks
const createWrappableDescription = (description) => {
  if (!description || description.length === 0) return description;
  
  // For description column (40% of page width), estimate characters per line
  // Conservative estimate to ensure text wraps properly within column bounds
  const descCharsPerLine = 45; // Conservative to force proper wrapping
  
  // If description is short enough for one line, return as-is
  if (description.length <= descCharsPerLine) {
    return description;
  }
  
  // For longer descriptions, force line breaks at word boundaries
  const words = description.split(' ');
  let result = '';
  let currentLine = '';
  
  for (const word of words) {
    // Check if adding this word would exceed line length
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= descCharsPerLine) {
      currentLine = testLine;
    } else {
      // Start new line
      if (result) result += '\n';
      result += currentLine;
      currentLine = word;
    }
  }
  
  // Add the last line
  if (currentLine) {
    if (result) result += '\n';
    result += currentLine;
  }
  
  return result;
};

// Create wrappable type text (for Type column)
const createWrappableType = (type) => {
  if (!type || type.length === 0) return type;
  
  // Type column is 18% of page width, estimate ~15 characters per line
  const typeCharsPerLine = 15;
  
  if (type.length <= typeCharsPerLine) {
    return type;
  }
  
  // For longer types, try to break at word boundaries or truncate
  if (type.includes(' ')) {
    const words = type.split(' ');
    let result = '';
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      
      if (testLine.length <= typeCharsPerLine) {
        currentLine = testLine;
      } else {
        if (result) result += '\n';
        result += currentLine;
        currentLine = word;
      }
    }
    
    if (currentLine) {
      if (result) result += '\n';
      result += currentLine;
    }
    
    return result;
  }
  
  // For single long words, truncate with ellipsis
  return type.length > typeCharsPerLine ? type.substring(0, typeCharsPerLine - 1) + '…' : type;
};

// Calculate dynamic row height based on actual line breaks in content
const calculateTransactionRowHeight = (description, type) => {
  // Calculate lines needed for description (with forced line breaks)
  const descriptionLines = description ? (description.split('\n').length) : 1;
  
  // Calculate lines needed for type (with potential line breaks)  
  const typeLines = type ? (type.split('\n').length) : 1;
  
  // Use the maximum lines needed by any column
  const maxLines = Math.max(descriptionLines, typeLines);
  
  // Different height calculation for single vs multi-line
  if (maxLines === 1) {
    return 18; // Standard single-line height
  } else {
    // For multi-line: base height + additional height per extra line
    const baseHeight = 18; // Height for first line
    const additionalLineHeight = 12; // Height for each additional line (tighter)
    const calculatedHeight = baseHeight + ((maxLines - 1) * additionalLineHeight);
    
    console.log(`[Transaction Row Height] Description: "${(description || '').substring(0, 40)}..." | Desc Lines: ${descriptionLines} | Type Lines: ${typeLines} | Max Lines: ${maxLines} | Height: ${calculatedHeight}pt`);
    return calculatedHeight;
  }
};

// Advanced pagination function that accounts for description text wrapping
const paginateTransactions = (transactions) => {
  console.log(`[Pagination] Starting dynamic pagination for ${transactions.length} transactions`);
  
  const pages = [];
  let currentPage = [];
  let currentPageHeight = 0;
  let isFirstPage = true;
  
  // Available heights with optimized safety buffer to prevent footer overlap
  const safetyBuffer = 18; // Increased to prevent footer overlap on pages 2 and 3
  const firstPageAvailableHeight = FIRST_PAGE_CONTENT_HEIGHT - TABLE_HEADER_HEIGHT - SUMMARY_HEIGHT - safetyBuffer;
  const continuationPageAvailableHeight = CONTENT_HEIGHT - TABLE_HEADER_HEIGHT - safetyBuffer;
  
  console.log(`[Pagination] First page available: ${firstPageAvailableHeight}pt`);
  console.log(`[Pagination] Continuation page available: ${continuationPageAvailableHeight}pt`);
  
  transactions.forEach((transaction, index) => {
    const rowHeight = calculateTransactionRowHeight(transaction.description, transaction.type);
    const availableHeight = isFirstPage ? firstPageAvailableHeight : continuationPageAvailableHeight;
    
    // Check if this transaction fits on current page
    if (currentPageHeight + rowHeight > availableHeight && currentPage.length > 0) {
      // Start new page
      console.log(`[Pagination] Page ${pages.length + 1} complete: ${currentPage.length} transactions, ${currentPageHeight}pt used of ${availableHeight}pt available`);
      pages.push([...currentPage]);
      currentPage = [transaction];
      currentPageHeight = rowHeight;
      isFirstPage = false;
    } else {
      // Add to current page
      currentPage.push(transaction);
      currentPageHeight += rowHeight;
    }
    
    // Store calculated height with transaction for rendering
    transaction.calculatedHeight = rowHeight;
  });
  
  // Add final page
  if (currentPage.length > 0) {
    console.log(`[Pagination] Final page ${pages.length + 1}: ${currentPage.length} transactions, ${currentPageHeight}pt used`);
    pages.push(currentPage);
  }
  
  // Handle empty transactions
  if (transactions.length === 0) {
    pages.push([]);
  }
  
  console.log(`[Pagination] Dynamic pagination complete: ${pages.length} pages`);
  return pages;
};

// Clean PDF Template Component
const CleanStatementPDF = React.memo(({ data, dateRange, clientName, clientAddress, clinicInfo }) => {
  console.log('[Clean Statement PDF] Starting generation with dynamic height calculation');
  console.log(`[Clean Statement PDF] Using Courier font with 73 chars/line calculation`);
  console.log('[Clean Statement PDF] Split billing context:', data?.splitBillingContext);
  
  // Process and clean transaction data
  const transactions = useMemo(() => {
    if (!data?.transactions) return [];
    
    // Deduplicate transactions
    const uniqueTransactions = [];
    const seenIds = new Set();
    
    data.transactions.forEach((trans) => {
      const transId = trans._id || `${trans.type}_${trans.date}_${trans.amount}`;
      if (!seenIds.has(transId)) {
        seenIds.add(transId);
        uniqueTransactions.push(trans);
      }
    });
    
    // Clean payment method descriptions
    const cleanPaymentMethod = (description) => {
      if (!description) return description;
      const methodMap = {
        'mastercard-online': 'Mastercard (Online)',
        'visa-online': 'Visa (Online)',
        'email-money-transfer': 'Email Money Transfer',
        'direct-deposit': 'Direct Deposit',
      };
      
      let cleaned = description;
      Object.entries(methodMap).forEach(([key, value]) => {
        cleaned = cleaned.replace(new RegExp(key, 'gi'), value);
      });
      return cleaned;
    };

    // Process transactions into display format
    return uniqueTransactions.map((trans) => {
      const rawDate = new Date(trans.date);
      const date = new Date(rawDate.getUTCFullYear(), rawDate.getUTCMonth(), rawDate.getUTCDate());
      let type, description, charges = 0, payments = 0;
      
      switch (trans.type) {
        case 'invoice':
          type = trans.status === 'refunded' ? 'Invoice (REFUNDED)' : 'Invoice';
          description = (trans.invoiceId ? `${trans.invoiceId} - ` : '') + trans.description;
          charges = trans.amount;
          break;
        case 'payment':
          type = 'Payment';
          description = cleanPaymentMethod(trans.description);
          payments = Math.abs(trans.amount);
          break;
        case 'payment_refund':
          type = 'Refund';
          description = cleanPaymentMethod(trans.description);
          charges = Math.abs(trans.amount);
          break;
        case 'deposit':
          type = 'Deposit';
          description = cleanPaymentMethod(trans.description);
          payments = Math.abs(trans.amount);
          break;
        case 'deposit_refund':
          type = 'Deposit Refund';
          description = cleanPaymentMethod(trans.description);
          charges = Math.abs(trans.amount);
          break;
        case 'deposit_usage':
          type = 'Deposit Applied';
          description = `Deposit Applied - ${cleanPaymentMethod(trans.description.replace('Deposit Applied - ', ''))}` + 
                       (trans.invoiceId ? ` to ${trans.invoiceId}` : '');
          payments = 0; // Don't double count
          break;
        default:
          type = trans.type;
          description = cleanPaymentMethod(trans.description);
          charges = trans.amount > 0 ? trans.amount : 0;
          payments = trans.amount < 0 ? Math.abs(trans.amount) : 0;
      }
      
      // Ensure description and type have proper line breaks for wrapping
      const wrappableDescription = createWrappableDescription(description);
      const wrappableType = createWrappableType(type);
      
      return { date, type: wrappableType, description: wrappableDescription, charges, payments, _id: trans._id };
    }).sort((a, b) => a.date - b.date);
  }, [data]);

  // Calculate totals
  const totalCharges = useMemo(() => transactions.reduce((sum, t) => sum + t.charges, 0), [transactions]);
  const totalPayments = useMemo(() => transactions.reduce((sum, t) => sum + t.payments, 0), [transactions]);
  const balanceDue = totalCharges - totalPayments;

  // Paginate transactions
  const paginatedTransactions = useMemo(() => paginateTransactions(transactions), [transactions]);

  return (
    <Document>
      {paginatedTransactions.map((pageTransactions, pageIndex) => (
        <Page key={pageIndex} size="LETTER" style={styles.page}>
          {/* Header - Fixed on every page */}
          <View style={styles.header} fixed>
            <Image
              style={styles.logo}
              src={clinicInfo?.logo || `${process.env.PUBLIC_URL}/listenhear_logo.png`}
            />
            <View style={styles.clinicInfo}>
              <Text style={styles.clinicName}>
                {clinicInfo?.name || ""}
              </Text>
              <Text style={styles.clinicDetails}>
                {clinicInfo?.address?.street || ""}
              </Text>
              <Text style={styles.clinicDetails}>
                {clinicInfo?.address?.city || ""}, {clinicInfo?.address?.state || ""} {clinicInfo?.address?.zipCode || ""}
              </Text>
              <Text style={styles.clinicDetails}>
                Phone: {clinicInfo?.phoneNumber || ""}
              </Text>
              <Text style={styles.clinicDetails}>
                Email: {clinicInfo?.email || ""}
              </Text>
            </View>
          </View>
          
          <Text style={styles.title}>
            {data?.splitBillingContext?.hasSplitBilling ? 'COMBINED CLIENT STATEMENT' : 'CLIENT STATEMENT'}
          </Text>

          {/* Client info - Only on first page */}
          {pageIndex === 0 && (
            <View style={styles.clientInfoSection}>
              <Text style={styles.dateRange}>
                Period: {dateRange.start} to {dateRange.end}
              </Text>
              
              {data?.splitBillingContext?.hasSplitBilling ? (
                // Split billing layout with two columns
                <View style={styles.clientInfoRow}>
                  <View style={styles.primaryClientColumn}>
                    <Text style={[styles.clientInfo, { fontWeight: 'bold' }]}>
                      Primary Client:
                    </Text>
                    <Text style={styles.clientInfo}>
                      {clientName}
                    </Text>
                    {clientAddress && (
                      <>
                        <Text style={styles.clientInfo}>
                          {clientAddress.streetNumber ? `${clientAddress.streetNumber} ` : ''}
                          {clientAddress.streetName || ''}
                          {clientAddress.aptUnit ? `, Unit ${clientAddress.aptUnit}` : ''}
                        </Text>
                        <Text style={styles.clientInfo}>
                          {clientAddress.city || ''}{clientAddress.state ? `, ${clientAddress.state}` : ''} {clientAddress.zipCode || ''}
                        </Text>
                      </>
                    )}
                  </View>

                  {data.splitBillingContext.relatedClients.length > 0 && (
                    <View style={styles.relatedClientsColumn}>
                      <Text style={styles.relatedClientsTitle}>
                        Related Clients:
                      </Text>
                      {data.splitBillingContext.relatedClients.map((relatedClient, index) => (
                        <Text key={index} style={styles.relatedClientItem}>
                          • {relatedClient.name}
                        </Text>
                      ))}
                    </View>
                  )}
                </View>
              ) : (
                // Standard single client layout
                <>
                  <Text style={styles.clientInfo}>
                    {clientName}
                  </Text>
                  {clientAddress && (
                    <>
                      <Text style={styles.clientInfo}>
                        {clientAddress.streetNumber ? `${clientAddress.streetNumber} ` : ''}
                        {clientAddress.streetName || ''}
                        {clientAddress.aptUnit ? `, Unit ${clientAddress.aptUnit}` : ''}
                      </Text>
                      <Text style={styles.clientInfo}>
                        {clientAddress.city || ''}{clientAddress.state ? `, ${clientAddress.state}` : ''} {clientAddress.zipCode || ''}
                      </Text>
                    </>
                  )}
                </>
              )}
            </View>
          )}

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.dateCol, styles.tableHeaderText]}>Date</Text>
            <Text style={[styles.typeCol, styles.tableHeaderText]}>Type</Text>
            <Text style={[styles.descCol, styles.tableHeaderText]}>Description</Text>
            <Text style={[styles.chargeCol, styles.tableHeaderText]}>Charges</Text>
            <Text style={[styles.paymentCol, styles.tableHeaderText]}>Payments</Text>
          </View>

          {/* Transaction Rows with dynamic height */}
          {pageTransactions.length > 0 ? (
            pageTransactions.map((transaction, index) => (
              <View key={index} style={[styles.row, { height: transaction.calculatedHeight || ROW_HEIGHT }]}>
                <Text style={styles.dateCol}>
                  {transaction.date.toLocaleDateString("en-US", { timeZone: "UTC" })}
                </Text>
                <Text style={styles.typeCol}>{transaction.type}</Text>
                <Text style={styles.descCol}>{transaction.description}</Text>
                <Text style={styles.chargeCol}>
                  {transaction.charges > 0 ? `$${transaction.charges.toFixed(2)}` : ''}
                </Text>
                <Text style={styles.paymentCol}>
                  {transaction.payments > 0 ? `$${transaction.payments.toFixed(2)}` : ''}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.noTransactionsRow}>
              <Text style={styles.noTransactionsText}>
                No transactions found for the selected date range.
              </Text>
            </View>
          )}

          {/* Summary - Only on last page */}
          {pageIndex === paginatedTransactions.length - 1 && (
            <View style={styles.summarySection}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Charges:</Text>
                <Text style={styles.summaryValue}>${totalCharges.toFixed(2)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Payments:</Text>
                <Text style={styles.summaryValue}>${totalPayments.toFixed(2)}</Text>
              </View>
              <View style={[styles.summaryRow, styles.balanceRow]}>
                <Text style={styles.summaryLabel}>Balance Due:</Text>
                <Text style={styles.summaryValue}>${balanceDue.toFixed(2)}</Text>
              </View>
            </View>
          )}

          {/* Footer - Fixed on every page */}
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>
              © 2025 {clinicInfo?.name || "HearSay Speech & Hearing Centre - Milton"}. All rights reserved.
            </Text>
            <Text style={styles.footerText}>
              Phone: {clinicInfo?.phoneNumber || "905-875-3345"} • Email: {clinicInfo?.email || "milton@hearsay.ca"}
            </Text>
          </View>
        </Page>
      ))}
    </Document>
  );
});

CleanStatementPDF.displayName = 'CleanStatementPDF';

// Legacy StatementPDF - Kept for reference but not used
const StatementPDF = React.memo(({ data, dateRange, clientName, clientAddress, clinicInfo }) => {
  // This component is deprecated - use CleanStatementPDF instead
  return null;
});

StatementPDF.displayName = 'StatementPDF';

// Legacy SimpleStatementPDF - Not used anymore
const SimpleStatementPDF = React.memo(({ data, dateRange, clientName, clientAddress, clinicInfo }) => {
  // This component is deprecated - use CleanStatementPDF instead
  return null;
});

SimpleStatementPDF.displayName = 'SimpleStatementPDF';

export default function ClientStatementReport() {
  const dispatch = useDispatch();
  const [selectedClient, setSelectedClient] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // 30 days ago
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);

  const handleClientSelection = (client) => {
    setSelectedClient({
      _id: client._id,
      name: `${client.firstName} ${client.lastName}`,
      address: client.address,
      email: client.email,
      phoneNumbers: client.phoneNumbers,
    });
  };

  const handleGenerateReport = async () => {
    console.log('[Client Statement] Starting report generation');
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[Client Statement] Fetching statement data for client:', selectedClient._id);
      console.log('[Client Statement] Date range:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      const response = await dispatch(
        fetchClientStatement({
          clientId: selectedClient._id,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        })
      ).unwrap();
      
      console.log('[Client Statement] API response received:', response);

      if (!response || !response.data) {
        throw new Error("Invalid response data received");
      }

      // Update client data from response if available
      if (response.data.client) {
        setSelectedClient((prevClient) => ({
          ...prevClient,
          name: `${response.data.client.firstName} ${response.data.client.lastName}`,
          address: response.data.client.address,
          email: response.data.client.email,
          phoneNumbers: response.data.client.phoneNumbers,
        }));
      }

      console.log('[Client Statement] Setting report data');
      setReportData(response.data);
      
      // Add delay before enabling PDF rendering to prevent loops
      setTimeout(() => {
        console.log('[Client Statement] Enabling PDF render');
        setPdfReady(true);
      }, 100);
    } catch (err) {
      console.error('[Client Statement] Error generating report:', err);
      setError(
        "Failed to generate report: " + (err.message || "Unknown error")
      );
      setReportData(null);
      setPdfReady(false);
    } finally {
      setIsLoading(false);
      console.log('[Client Statement] Report generation completed');
    }
  };

  return (
    <div className="p-4">
      <div className="bg-white rounded-sm shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Client Statement Report</h2>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">
              Select Client
            </div>
            <SearchDropdown
              prefix=":client:"
              displayFields={["firstName", "lastName", "email"]}
              onSelection={handleClientSelection}
              placeholder="Search for a client"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Start Date
              </div>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">
                End Date
              </div>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className={`w-full py-2 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white 
              ${
                isLoading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
              }`}>
            {isLoading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && !pdfReady && (
        <div className="bg-white rounded-sm shadow p-6">
          <div className="flex items-center justify-center p-8">
            <div className="text-gray-600">Preparing PDF...</div>
          </div>
        </div>
      )}

      {reportData && pdfReady && (
        <div className="bg-white rounded-sm shadow p-6">
          {reportData?.splitBillingContext?.hasSplitBilling && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
              <div className="text-blue-800 font-medium text-sm">
                📋 Combined Statement Generated
              </div>
              <div className="text-blue-700 text-sm mt-1">
                This statement includes transactions from related split billing clients: 
                {reportData.splitBillingContext.relatedClients.map(client => client.name).join(', ')}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <PDFDownloadLink
              document={
                <CleanStatementPDF
                  data={reportData}
                  dateRange={{
                    start: startDate.toLocaleDateString("en-US", { timeZone: "UTC" }),
                    end: endDate.toLocaleDateString("en-US", { timeZone: "UTC" }),
                  }}
                  clientName={selectedClient.name}
                  clientAddress={selectedClient.address}
                  clinicInfo={reportData.clinic}
                />
              }
              fileName={`${reportData?.splitBillingContext?.hasSplitBilling ? 'combined-' : ''}client-statement-${selectedClient.name}.pdf`}
              className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              {({ loading }) => (loading ? "Preparing PDF..." : "Download PDF")}
            </PDFDownloadLink>

            <div className="w-full h-[600px] border rounded">
              <PDFViewer style={{ width: '100%', height: '100%' }}>
                <CleanStatementPDF
                  data={reportData}
                  dateRange={{
                    start: startDate.toLocaleDateString("en-US", { timeZone: "UTC" }),
                    end: endDate.toLocaleDateString("en-US", { timeZone: "UTC" }),
                  }}
                  clientName={selectedClient.name}
                  clientAddress={selectedClient.address}
                  clinicInfo={reportData.clinic}
                />
              </PDFViewer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
